import { useState } from "react";
import { useDispatch } from "react-redux";
import { Avatar, Button, Col, Form, Input, Modal, Row } from "antd";

import { PageWrapper, TextField } from "@/components";
import { useAuth, useNotification } from "@/hooks";
import { accountActions } from "@/redux/actions";
import { cleanObject, getErrMsgFromApi } from "@/utils";
import { validationEmail, validationPassword, validationPhone } from "@/utils/validator";

import { FileImageTwoTone, LockOutlined } from '@ant-design/icons';

import styles from "./index.module.scss";

const ProfilePage = () => {
    const dispatch = useDispatch();
    const [passwordForm] = Form.useForm();
    const {showErrorMessage, showSuccessMessage} = useNotification();
    const {user} = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    
    const onUploadAvatar = (e) => {
        const file = e.target.files[0];

        if(file.size > 2 * 1024 * 1024){
            showErrorMessage('Avatar không được lớn hơn 2mb!');
            return; 
        }
        showSuccessMessage('Thay đổi avatar thành công.');
    }

    const onSubmit = (values) => {
        dispatch(accountActions.updateProfile({
            params: cleanObject(values),
            onCompleted: (res) => {
                if(res.result){
                    dispatch(accountActions.updateProfileLocal(res))
                    showSuccessMessage("Update profile successfully!")
                }
            },
            onError: (res) => {
                showErrorMessage(getErrMsgFromApi(res));
            }
        }))
    }

    const onSubmitPassword = (values) => {
        dispatch(accountActions.changePassword({
            params: {password: values.password, oldPassword: values.oldPassword},
            onCompleted: (res) => {
                if(res.result){
                    showSuccessMessage(res.message);
                    setIsOpen(false);
                    passwordForm.resetFields();
                }
            },
            onError: (res) => {
                showErrorMessage(getErrMsgFromApi(res));
            }
        }))
    }

    return (
        <PageWrapper
            breadcrumbs={[
                { breadcrumbName: 'Profile'},
            ]}>
                <Row gutter={16} className={styles.profile}>
                    <Col span={12}>
                        <div className={styles.content}>
                            <label className="cursor-pointer">
                                <Avatar
                                    icon={<FileImageTwoTone />}
                                    src={user.avatar}
                                    size={100}/>
                                <input type="file" hidden accept="image/*" onChange={onUploadAvatar} disabled/>
                            </label>
                            <h1>{user.fullName}</h1>
                            <h4>{user.role?.name}</h4>
                            <Button type="primary" onClick={()=> {setIsOpen(true); passwordForm.resetFields()}}>Change password</Button>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.content}>
                            <h3>INFORMATION</h3>
                            <Form
                                id={'profile'}
                                name="12312312"
                                onFinish={onSubmit}
                                layout="vertical"
                                initialValues={user}
                            >
                                <Row gutter={16} justify={"center"}>
                                    <Col span={20}>
                                        <TextField
                                            fieldName="email"
                                            label="E-mail"
                                            maxLength={64}
                                            validators={validationEmail()}
                                            required
                                            disabled
                                        />
                                    </Col>
                                    <Col span={20}>
                                        <TextField
                                            fieldName="phoneNumber"
                                            label="Phone Number"
                                            type="tel"
                                            onlyNumber
                                            validators={validationPhone()}
                                        />
                                    </Col>
                                    <Col span={20}>
                                        <TextField
                                            fieldName="username"
                                            label="Username"
                                            maxLength={50}
                                            required
                                            notAllowWhiteSpace
                                            disabled
                                        />
                                    </Col>
                                    <Col span={10}>
                                        <TextField
                                            fieldName="firstName"
                                            label="First Name"
                                            maxLength={45}
                                            required
                                            notAllowWhiteSpace
                                        />
                                    </Col>
                                    <Col span={10}>
                                        <TextField
                                            fieldName="lastName"
                                            label="Last Name"
                                            maxLength={45}
                                            notAllowWhiteSpace
                                        />
                                    </Col>
                                </Row>
                                <Row justify={"center"}>
                                    <Button htmlType="submit" type="primary" style={{minWidth: 100}}>Save</Button>
                                </Row>
                            </Form>
                        </div>
                    </Col>
                </Row>

                <Modal 
                    open={isOpen} 
                    title="CHANGE PASSWORD"
                    onCancel={()=>setIsOpen(false)} 
                    footer={[
                            <Button key="change-pass" type="primary" htmlType="submit" form="change-password">Change password</Button>
                    ]}>
                    <Form 
                        id="change-password"
                        form={passwordForm}
                        onFinish={onSubmitPassword}
                        layout="vertical">
                        <Row>
                            <Col span={24}>
                                <Form.Item
                                    label="Old Password"
                                    name="oldPassword"
                                    rules={[{ required: true, message: 'Please enter old password!'}]}
                                >
                                    <Input.Password
                                        placeholder="Old Password"
                                        prefix={<LockOutlined />}
                                        autoComplete="off"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="New Password"
                                    name="password"
                                    rules={validationPassword("New Password")}
                                >
                                    <Input.Password
                                        placeholder="New Password"
                                        prefix={<LockOutlined />}
                                        autoComplete="off"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    rules={[
                                        { required: true, message: 'Please enter confirm password' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Old password and confirm password is not match'));
                                            },
                                            }),
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="Confirm Password"
                                        prefix={<LockOutlined />}
                                        autoComplete="off"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
        </PageWrapper>
    )
}

export default ProfilePage;